import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import CoursePlayerSidebar from "../components/course-player/CoursePlayerSidebar"
import CoursePlayerHeader from "../components/course-player/CoursePlayerHeader"
import LessonViewer from "../components/course-player/LessonViewer"
import OverviewTab from "../components/course-player/OverviewTab"
import ExerciseFilesTab from "../components/course-player/ExerciseFilesTab"
import QuizViewer from "../components/course-player/QuizViewer"

const BASE_URL = process.env.REACT_APP_API_BASE_URL

const CoursePlayerPage = () => {
  const { id, lessonId } = useParams()
  const navigate = useNavigate()
  const [courseData, setCourseData] = useState(null)
  const [currentLesson, setCurrentLesson] = useState(null)
  const [activeTab, setActiveTab] = useState("overview")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [sidebarOpen, setSidebarOpen] = useState(false);


  // Fetch course data from API
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const token = localStorage.getItem('access')
        const response = await fetch(`${BASE_URL}/courses/${id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch course data')
        }

        const data = await response.json()
        setCourseData(data)
        setLoading(false)
      } catch (err) {
        setError(err.message)
        setLoading(false)
      }
    }

    if (id) {
      fetchCourseData()
    }
  }, [id])

  // Set current lesson based on URL parameter or first available lesson
  useEffect(() => {
    if (courseData && courseData.modules) {
      if (lessonId) {
        // Find lesson by ID from URL
        const lesson = courseData.modules
          .flatMap(module => module.lessons)
          .find(lesson => lesson.id.toString() === lessonId)
        setCurrentLesson(lesson)
      } else {
        // Default to first lesson
        const firstLesson = courseData.modules
          .find(module => module.lessons && module.lessons.length > 0)?.lessons[0]
        setCurrentLesson(firstLesson)
      }
    }
  }, [courseData, lessonId])

  const handleLessonSelect = async (lesson) => {
    setCurrentLesson(lesson)
    navigate(`/course/${id}/learn/${lesson.id}`)
    
    // Mark lesson progress if it's not completed
    if (!lesson.completed) {
      try {
        const token = localStorage.getItem('access')
        await fetch(`${BASE_URL}/courses/lessons/${lesson.id}/progress/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        
        // Update local state to reflect completion
        setCourseData(prevData => ({
          ...prevData,
          modules: prevData.modules.map(module => ({
            ...module,
            lessons: module.lessons.map(l => 
              l.id === lesson.id ? { ...l, completed: true } : l
            )
          }))
        }))
      } catch (err) {
        //console.error('Failed to update lesson progress:', err)
      }
    }
  }

  const handleClose = () => {
    navigate(`/course/${id}`)
  }

  const renderMainContent = () => {
    if (currentLesson?.content_type === "quiz") {
      return <QuizViewer lesson={currentLesson} />
    }

    return (
      <LessonViewer
        lesson={currentLesson}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        renderTabContent={() => {
          switch (activeTab) {
            case "overview":
              return <OverviewTab lesson={currentLesson} />
            case "exercise-files":
              return <ExerciseFilesTab lesson={currentLesson} />
            default:
              return <OverviewTab lesson={currentLesson} />
          }
        }}
      />
    )
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden"></span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger m-4">
        <h4>Error Loading Course</h4>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Go Back to Home
        </button>
      </div>
    )
  }

  if (!courseData) {
    return (
      <div className="alert alert-warning m-4">
        <h4>Course Not Found</h4>
        <p>The requested course could not be found.</p>
      </div>
    )
  }

  return (
    <div className="course-player">
      <CoursePlayerHeader course={courseData} onClose={handleClose} onToggleSidebar={() => setSidebarOpen(prev => !prev)} />
      <div className="course-player-body">
        <CoursePlayerSidebar 
          course={courseData} 
          currentLesson={currentLesson} 
          onLessonSelect={handleLessonSelect} 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <div className="course-player-main">{renderMainContent()}</div>
      </div>
    </div>
  )
}

export default CoursePlayerPage
